import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'approach', label: t('nav.approach') },
    { id: 'how-it-works', label: t('nav.howItWorks') },
    { id: 'why-portugal', label: t('nav.whyPortugal') },
  ];

  return (
    <nav 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'navbar-scrolled py-4' : 'bg-transparent py-6'
      }`}
      data-testid="navbar"
    >
      <div className="container-custom flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <img 
            src="/logo.svg" 
            alt="Portugal Elite Estates" 
            className="w-10 h-10"
          />
          <div className="hidden sm:block">
            <span className={`font-heading text-lg font-semibold tracking-tight ${isScrolled ? 'text-foreground' : 'text-white'}`}>
              Portugal Elite
            </span>
            <span className={`block text-[10px] tracking-[0.2em] uppercase ${isScrolled ? 'text-muted-foreground' : 'text-white/70'}`}>
              Estates
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-10">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
                isScrolled
                  ? 'text-foreground/80 hover:text-primary'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              {link.label}
            </button>
          ))}
        </div>

        {/* CTA Button */}
        <div className="hidden lg:block">
          <Link to="/start-your-search">
            <Button 
              data-testid="nav-cta-button"
              className={`rounded-full px-6 h-11 font-medium text-sm tracking-wide transition-all duration-300 ${
                isScrolled 
                  ? 'bg-primary text-white hover:bg-primary/90' 
                  : 'bg-white text-primary hover:bg-white/90'
              }`}
            >
              {t('nav.startSearch')}
            </Button>
          </Link>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`lg:hidden p-2 ${isScrolled ? 'text-foreground' : 'text-white'}`}
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl border-t" data-testid="mobile-menu">
          <div className="container-custom py-8 space-y-2">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="block w-full text-left py-3 px-4 text-foreground hover:text-primary transition-colors font-medium"
              >
                {link.label}
              </button>
            ))}
            <div className="pt-4">
              <Link 
                to="/start-your-search" 
                onClick={() => setIsMobileMenuOpen(false)}
                className="block"
              >
                <Button className="w-full btn-primary">
                  {t('nav.startSearch')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
